import { FC, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { addGameSearchParams } from 'src/helpers/gameQueriesManager';
import { useConnectedAction } from 'src/hooks/use-connected-action';
import { setSelectedGame } from 'src/store/games/actions';
import { RootState } from 'src/types/store-types';
import './styles.scss';

// show the "continue playing" prompt this long after the player closes a game
const SHOW_AFTER_CLOSE_MS = 5_000;
// auto-dismiss the prompt if the player ignores it, so it doesn't linger forever
const AUTO_DISMISS_MS = 10_000;

const ContinuePlayingNotification: FC = () => {
  const { t }: Translation = useTranslation();
  const [searchParams, setQueryParams] = useSearchParams();
  const _selectedGame = useConnectedAction(setSelectedGame);

  const { game } = useSelector((state: RootState) => state.games);
  const { user } = useSelector((state: RootState) => state.user);

  // the game shown in the prompt (null = hidden)
  const [pendingGame, setPendingGame] = useState<any>(null);
  // the game that was open on the previous render, so we can detect a close (game → null)
  const prevGameRef = useRef<any>(null);
  const showTimerRef = useRef<ReturnType<typeof setTimeout>>();

  const clearShowTimer = (): void => {
    if (showTimerRef.current) clearTimeout(showTimerRef.current);
  };

  useEffect(() => {
    const closedGame = prevGameRef.current;
    prevGameRef.current = game;

    // a game is open again: cancel a scheduled prompt and hide any shown one
    if (game) {
      clearShowTimer();
      setPendingGame(null);
      return;
    }

    // a game just closed (was open, now null): show the prompt after the delay
    if (closedGame && user) {
      clearShowTimer();
      showTimerRef.current = setTimeout(() => setPendingGame(closedGame), SHOW_AFTER_CLOSE_MS);
    }
  }, [game, user]);

  // drop a scheduled/visible prompt on logout
  useEffect(() => {
    if (!user) {
      clearShowTimer();
      setPendingGame(null);
    }
  }, [user]);

  // auto-dismiss once shown
  useEffect(() => {
    if (!pendingGame) return;
    const id = setTimeout(() => setPendingGame(null), AUTO_DISMISS_MS);
    return () => clearTimeout(id);
  }, [pendingGame]);

  // clear the pending timer on unmount
  useEffect(() => () => clearShowTimer(), []);

  if (!pendingGame) return null;

  const image = pendingGame.thumbnail || pendingGame.icon || pendingGame.image;

  const handleContinue = (): void => {
    setPendingGame(null);
    _selectedGame(pendingGame);
    addGameSearchParams(searchParams, setQueryParams, pendingGame);
  };

  const handleClose = (): void => {
    setPendingGame(null);
  };

  return (
    <div className="continue-playing" role="alert">
      {image && (
        <div className="continue-playing__thumb">
          <img src={image} alt={pendingGame.name} />
        </div>
      )}
      <div className="continue-playing__body">
        <span className="continue-playing__title">{t('backToGame')}</span>
        {pendingGame.name && (
          <button type="button" className="continue-playing__name" onClick={handleContinue}>
            {pendingGame.name}
          </button>
        )}
      </div>

      <button className="continue-playing__close" onClick={handleClose} aria-label="Close">
        &times;
      </button>
    </div>
  );
};

export default ContinuePlayingNotification;
